import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import cls from 'classnames/bind';

import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';

import FormattedDate from '@ott/formatted-date';
import FormattedPeriod, {
  normalDelimiter,
  narrowNonBreakingDelimiter,
} from '@ott/formatted-period';
import { l10n, l10nhtml } from '@ott/l10n';

import s from './DatesFormatter.scss';

const cx = cls.bind(s);

const ShortFormattedDate = (props) => (
  <FormattedDate isShortMonth={!props.forceLongMonth} {...props} />
);

const ShortFormattedPeriod = (props) => <FormattedPeriod isShortMonth={true} {...props} />;

const isTomorrow = (date) => {
  const tomorrow = addDays(new Date(), 1);
  return (
    date?.getDate() === tomorrow.getDate() &&
    date?.getMonth() === tomorrow.getMonth() &&
    date?.getFullYear() === tomorrow.getFullYear()
  );
};

class DatesFormatter extends PureComponent {
  static propTypes = {
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    chosenTime: PropTypes.string,
    hoveredDate: PropTypes.instanceOf(Date),
    hoveredTime: PropTypes.string,
    foldSameDate: PropTypes.bool,
    endDateIsOptional: PropTypes.bool,
    showNights: PropTypes.bool,
    showYear: PropTypes.bool,
    showNextYear: PropTypes.bool,
    shouldShowTomorrow: PropTypes.bool,
    pickOneDate: PropTypes.bool,
    placeholder: PropTypes.node,
    startDatePlaceholder: PropTypes.string,
    endDatePlaceholder: PropTypes.string,
    additionalInfo: PropTypes.node,
    isInvalid: PropTypes.bool,
    isShortMonth: PropTypes.bool,
    forceLongMonth: PropTypes.bool,
    childrenClassNames: PropTypes.shape({
      label: PropTypes.string,
    }),
  };

  static defaultProps = {
    foldSameDate: false,
    endDateIsOptional: false,
    showYear: false,
    showNextYear: true,
    forceLongMonth: false,
    childrenClassNames: {
      label: '',
    },
  };

  getNightsNode(startDate, endDate) {
    const nights = differenceInCalendarDays(endDate, startDate);

    return (
      <span>
        , {nights} {l10n('nights', { nights })}
      </span>
    );
  }

  getStartDateNode(startDate, endDate, showYear) {
    return startDate.getMonth() === endDate.getMonth() ? (
      startDate.getDate() // 20 - 25 авг
    ) : (
      <ShortFormattedDate date={startDate} showYear={showYear} />
    ); // 20 авг - 12 сент
  }

  getDelimiter(startDate, endDate) {
    return startDate.getMonth() === endDate.getMonth()
      ? narrowNonBreakingDelimiter
      : normalDelimiter;
  }

  getFormattedDates() {
    const {
      startDate,
      endDate,
      chosenTime,
      placeholder,
      foldSameDate,
      endDateIsOptional,
      showNights,
      showYear,
      showNextYear,
      pickOneDate,
      hoveredDate,
      hoveredTime,
      endDatePlaceholder,
      additionalInfo,
      forceLongMonth,
      childrenClassNames,
      shouldShowTomorrow,
    } = this.props;

    const currentYear = new Date().getFullYear();
    const isStartDateNextYear = !!startDate && startDate.getFullYear() > currentYear;
    const isEndDateNextYear = !!endDate && endDate.getFullYear() > currentYear;
    const isHoveredDateNextYear = !!hoveredDate && hoveredDate.getFullYear() > currentYear;

    if (!startDate && !endDate) {
      if (pickOneDate && chosenTime) {
        return (
          <span>
            <span className={cx('dimmed', childrenClassNames.label)}>
              {hoveredDate ? (
                <ShortFormattedDate
                  date={hoveredDate}
                  showYear={showNextYear ? isHoveredDateNextYear : false}
                />
              ) : null}
              ,&nbsp;
            </span>
            {chosenTime}
          </span>
        );
      }

      if (pickOneDate && hoveredTime) {
        return (
          <span className={cx('dimmed', childrenClassNames.label)}>
            {hoveredDate ? (
              <ShortFormattedDate
                date={hoveredDate}
                showYear={showNextYear ? isHoveredDateNextYear : false}
              />
            ) : null}
            , {hoveredTime}
          </span>
        );
      }

      // warning: здесь div специально вместо span
      // кейс: на сервервере - плейсхолдер, на клиенте - дата.
      // дата подставлялась корректно, но стиль оставался как у плейсхолдера
      return (
        <div className={cx('dimmed', childrenClassNames.label)}>
          {hoveredDate ? (
            shouldShowTomorrow && isTomorrow(hoveredDate) ? (
              l10nhtml('datepicker.tomorrow', {
                date: <ShortFormattedDate date={hoveredDate} forceLongMonth={forceLongMonth} />,
              })
            ) : (
              <ShortFormattedDate
                date={hoveredDate}
                showYear={showNextYear ? isHoveredDateNextYear : false}
                forceLongMonth={forceLongMonth}
              />
            )
          ) : (
            placeholder
          )}
        </div>
      );
    }

    if (pickOneDate) {
      if (chosenTime) {
        return (
          <span className={cx(childrenClassNames.label)}>
            <ShortFormattedDate
              date={startDate}
              showYear={showNextYear ? isStartDateNextYear : false}
            />
            , {chosenTime}
          </span>
        );
      }
      if (hoveredTime) {
        return (
          <span className={cx(childrenClassNames.label)}>
            <ShortFormattedDate
              date={startDate}
              showYear={showNextYear ? isStartDateNextYear : false}
            />
            <span className={cx('dimmed')}>{`, ${hoveredTime}`}</span>
          </span>
        );
      }
      return shouldShowTomorrow && isTomorrow(startDate) ? (
        l10nhtml('datepicker.tomorrow', {
          date: <ShortFormattedDate date={startDate} forceLongMonth={forceLongMonth} />,
        })
      ) : (
        <ShortFormattedDate
          date={startDate}
          showYear={showNextYear ? isStartDateNextYear : false}
          forceLongMonth={forceLongMonth}
        />
      );
    }

    if (startDate && endDate) {
      return (
        <span className={cx(childrenClassNames.label)}>
          <ShortFormattedPeriod
            showYear={showNextYear ? isEndDateNextYear : showYear}
            foldSameDate={foldSameDate}
            startDate={startDate}
            endDate={endDate}
          />
          <span className={cx('dimmed')}>
            {showNights && this.getNightsNode(startDate, endDate)}
            {additionalInfo}
          </span>
        </span>
      );
    }
    if (startDate) {
      if (hoveredDate) {
        if (startDate < hoveredDate) {
          return (
            <span className={cx(childrenClassNames.label)}>
              {this.getStartDateNode(startDate, hoveredDate)}
              <span className={cx('dimmed')}>
                {this.getDelimiter(startDate, hoveredDate)}
                <ShortFormattedDate
                  date={hoveredDate}
                  showYear={showNextYear ? isHoveredDateNextYear : false}
                />
                {showNights && this.getNightsNode(startDate, hoveredDate)}
                {additionalInfo}
              </span>
            </span>
          );
        }
        if (hoveredDate < startDate) {
          return (
            <span className={cx(childrenClassNames.label)}>
              <span className={cx('dimmed')}>
                {this.getStartDateNode(hoveredDate, startDate)}
                {this.getDelimiter(hoveredDate, startDate)}
              </span>
              <ShortFormattedDate
                date={startDate}
                showYear={showNextYear ? isStartDateNextYear : false}
              />
              <span className={cx('dimmed')}>
                {showNights && this.getNightsNode(hoveredDate, startDate)}
                {additionalInfo}
              </span>
            </span>
          );
        }
        // выбран только startDate
        return (
          <span className={cx(childrenClassNames.label)}>
            <ShortFormattedDate
              date={startDate}
              showYear={showNextYear ? isStartDateNextYear : false}
            />
            <span className={cx('dimmed')}>
              {normalDelimiter}
              {endDatePlaceholder}
            </span>
          </span>
        );
      }
      // инпут неактивен, выбран только startDate
      if (endDateIsOptional) {
        return (
          <span className={cx(childrenClassNames.label)}>
            <ShortFormattedDate
              date={startDate}
              showYear={showNextYear ? isStartDateNextYear : false}
            />
          </span>
        );
      }
      return (
        <span className={cx('dimmed', childrenClassNames.label)}>
          <ShortFormattedDate
            date={startDate}
            showYear={showNextYear ? isStartDateNextYear : false}
          />
          {normalDelimiter}
          {endDatePlaceholder}
        </span>
      );
    }
  }

  render() {
    const { isInvalid, childrenClassNames } = this.props;

    return (
      <span
        className={cx('DatesFormatter', childrenClassNames.label, {
          'DatesFormatter--isInvalid': isInvalid,
        })}
      >
        {this.getFormattedDates()}
      </span>
    );
  }
}

export default DatesFormatter;
